import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector, useAuth } from '../hooks';

import { TimesheetActions } from '../actions/timesheet/timesheet.actions';

import type { Timesheet } from '../interfaces/employee/timesheet.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import FilterChipStrip from './components/FilterChipStrip';
import { formatDate, formatMonthYear } from './lib/mandate-helpers';
import { useTableSort, SortableTh } from './lib/use-table-sort';

// =============================================================================
//  Timesheets — v3
//
//  The highest-volume table in the system (one row per person per client per
//  day), so this leans on filtering rather than trying to show everything:
//  month chips derived from the data, plus search.  Hours are totalled for
//  whatever is currently in view, which is the number people actually want.
//
//  Employees see only their own rows (API-scoped), so the Employee column is
//  hidden for them.
// =============================================================================

const monthKey = (t: Timesheet): string => {
  if (!t.date) return '';
  const d = t.date instanceof Date ? t.date : new Date(t.date);
  return Number.isNaN(d.getTime())
    ? ''
    : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const hoursOf = (t: Timesheet): number => {
  const n = Number(t.billable_hours ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const TimesheetsListV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isHrPrivileged } = useAuth();

  const timesheets: Timesheet[] =
    useAppSelector(({ timesheet_store }) => timesheet_store.timesheets) || [];

  const [loaded, setLoaded] = useState(timesheets.length > 0);
  const [monthFilter, setMonthFilter] = useState<string>('All');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    dispatch(TimesheetActions.loadTimesheets(() => setLoaded(true)));
  }, [dispatch]);

  const handleExport = () => {
    if (exporting) return;
    setExporting(true);
    dispatch(TimesheetActions.downloadTimesheets(() => setExporting(false)));
  };

  // Month chips come from the data, newest first, so they never go stale.
  const monthOptions = useMemo(() => {
    const keys = Array.from(new Set(timesheets.map(monthKey).filter(Boolean))).sort((a, b) =>
      b.localeCompare(a)
    );
    return [
      { value: 'All', label: 'All' },
      ...keys.slice(0, 12).map((k) => ({ value: k, label: formatMonthYear(`${k}-01`) }))
    ];
  }, [timesheets]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return timesheets.filter((t) => {
      if (monthFilter !== 'All' && monthKey(t) !== monthFilter) return false;
      if (q) {
        const hay =
          `${t.employee ?? ''} ${t.client ?? ''} ${t.category ?? ''} ${t.sub_category ?? ''} ${t.description ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [timesheets, monthFilter, search]);

  const SORT_ACCESSORS = useMemo(
    () => ({
      employee: (t: Timesheet) => t.employee,
      date: (t: Timesheet) => (t.date ? new Date(t.date as string) : null),
      client: (t: Timesheet) => t.client,
      category: (t: Timesheet) => [t.category, t.sub_category].filter(Boolean).join(' · '),
      description: (t: Timesheet) => t.description,
      hours: (t: Timesheet) => hoursOf(t)
    }),
    []
  );
  const { sorted, sort, toggle } = useTableSort(filtered, SORT_ACCESSORS, {
    key: 'date',
    dir: 'desc'
  });

  const totalHours = useMemo(() => sorted.reduce((s, t) => s + hoursOf(t), 0), [sorted]);

  const subLine = () => {
    if (!loaded && timesheets.length === 0) return 'Loading…';
    return [
      filtered.length === timesheets.length
        ? `${timesheets.length} entr${timesheets.length === 1 ? 'y' : 'ies'}`
        : `${filtered.length} of ${timesheets.length} entries`,
      totalHours > 0 ? `${totalHours.toFixed(1)} billable hours` : undefined
    ]
      .filter(Boolean)
      .join(' · ');
  };

  return (
    <>
      <Topbar
        crumbs={['Human Resources', 'Timesheets']}
        tools={
          <button
            className="btn ghost"
            type="button"
            onClick={handleExport}
            disabled={exporting || timesheets.length === 0}
            title={exporting ? 'Preparing spreadsheet…' : 'Download all timesheets'}
          >
            <Icon name="download" size={14} />
            {exporting ? 'Exporting…' : 'Export'}
          </button>
        }
      />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Human Resources</div>
            <h1 className="page-title">Timesheets</h1>
            <div className="page-sub">{subLine()}</div>
          </div>
          <div className="page-tools">
            <label className="search">
              <Icon name="search" size={14} />
              <input
                type="text"
                placeholder="Search entries…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            {/* Logging time is self-service.  Opens the legacy form, which
                also carries the "add another" rapid-entry flow. */}
            <button
              className="btn primary"
              type="button"
              onClick={() => navigate('/human-resources/timesheets/add')}
              title="Opens the legacy time-entry form"
            >
              <Icon name="plus" size={14} />
              Log time
            </button>
          </div>
        </div>

        {monthOptions.length > 1 && (
          <FilterChipStrip<string>
            options={monthOptions}
            value={monthFilter}
            onChange={setMonthFilter}
          />
        )}

        {sorted.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <span className="muted">
              {!loaded && timesheets.length === 0
                ? 'Loading timesheets…'
                : timesheets.length === 0
                  ? 'No time logged yet.'
                  : 'No entries match these filters.'}
            </span>
          </div>
        ) : (
          <div className="card flush">
            <table className="tbl">
              <thead>
                <tr>
                  {isHrPrivileged && (
                    <SortableTh label="Employee" sortKey="employee" sort={sort} onToggle={toggle} />
                  )}
                  <SortableTh label="Date" sortKey="date" sort={sort} onToggle={toggle} />
                  <SortableTh label="Client" sortKey="client" sort={sort} onToggle={toggle} />
                  <SortableTh label="Category" sortKey="category" sort={sort} onToggle={toggle} />
                  <SortableTh
                    label="Description"
                    sortKey="description"
                    sort={sort}
                    onToggle={toggle}
                  />
                  <SortableTh
                    label="Hours"
                    sortKey="hours"
                    sort={sort}
                    onToggle={toggle}
                    className="right"
                  />
                </tr>
              </thead>
              <tbody>
                {sorted.map((t) => (
                  <tr key={t.id}>
                    {isHrPrivileged && <td className="strong">{t.employee || '—'}</td>}
                    <td className="mono">{formatDate(t.date)}</td>
                    <td>{t.client || '—'}</td>
                    <td>
                      {/* sub_category is the more specific of the two when set */}
                      {[t.category, t.sub_category].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td>{t.description || <span className="muted">—</span>}</td>
                    <td className="num right strong">{hoursOf(t).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default TimesheetsListV3;
